import { en } from './en.js'
import { type Dictionary, type MessageKey, nl } from './nl.js'

export type Locale = 'nl' | 'en'

const STORAGE_KEY = 'streepaf.locale'
const DICTIONARIES: Record<Locale, Dictionary> = { nl, en }

function detect(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'nl' || stored === 'en') return stored
  } catch {
    // Private browsing can deny storage; the browser language still works.
  }
  const languages =
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
  return languages.some((language) => language?.toLowerCase().startsWith('nl')) ? 'nl' : 'en'
}

class I18n {
  locale = $state<Locale>('nl')

  init(): void {
    this.set(detect())
  }

  set(locale: Locale): void {
    this.locale = locale
    if (typeof document !== 'undefined') document.documentElement.lang = locale
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // Not being able to remember the choice is not worth an error.
    }
  }

  toggle(): void {
    this.set(this.locale === 'nl' ? 'en' : 'nl')
  }

  /** Interpolates {name} placeholders. */
  t(key: MessageKey, params?: Record<string, string | number>): string {
    const template = DICTIONARIES[this.locale][key] ?? nl[key]
    if (!params) return template
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in params ? String(params[name]) : match,
    )
  }

  date(value: string): string {
    return new Intl.DateTimeFormat(this.locale, { dateStyle: 'long' }).format(new Date(value))
  }

  /** "2 hours ago", picking the largest unit that fits. */
  relative(value: string): string {
    const diff = Date.parse(value) - Date.now()
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 31_536_000_000],
      ['month', 2_592_000_000],
      ['week', 604_800_000],
      ['day', 86_400_000],
      ['hour', 3_600_000],
      ['minute', 60_000],
    ]
    const formatter = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' })
    for (const [unit, ms] of units) {
      if (Math.abs(diff) >= ms) return formatter.format(Math.round(diff / ms), unit)
    }
    return formatter.format(0, 'minute')
  }
}

export const i18n = new I18n()
export const t = (key: MessageKey, params?: Record<string, string | number>) => i18n.t(key, params)
